import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';
import { sumBy } from '../../utils/insightStats.js';

import { BankImportModal } from './BankImportModal.jsx';
import { BankMatchModal } from './BankMatchModal.jsx';

const TAB_STATUS = { unmatched: 'UNMATCHED', matched: 'MATCHED', ignored: 'IGNORED' };

export function BankReconciliationPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('unmatched');
  const [importOpen, setImportOpen] = useState(false);
  const [matchTx, setMatchTx] = useState(null);
  const [ignoreTx, setIgnoreTx] = useState(null);

  const status = TAB_STATUS[tab];
  const transactions = useQuery({
    queryKey: ['finance', 'bank', status],
    queryFn: () => financeApi.listBankTransactions(status).then((r) => r.items),
  });

  const ignore = useMutation({
    mutationFn: (id) => financeApi.ignoreBankTransaction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'bank'] });
      setIgnoreTx(null);
    },
  });

  const rows = transactions.data || [];

  const palette = useChartPalette();
  const stats = useMemo(() => {
    const txRows = transactions.data || [];
    const directions = ['CREDIT', 'DEBIT'].filter((d) => txRows.some((r) => r.direction === d));
    const inflow = sumBy(txRows, (r) => r.amount, (r) => r.direction === 'CREDIT');
    const outflow = sumBy(txRows, (r) => r.amount, (r) => r.direction === 'DEBIT');
    return {
      count: txRows.length,
      directions,
      amountByDirection: directions.map((d) => sumBy(txRows, (r) => r.amount, (r) => r.direction === d)),
      inflow,
      outflow,
    };
  }, [transactions.data]);

  const directionBadge = (r) =>
    r.direction === 'CREDIT' ? (
      <Badge variant="success">{t('bank.dir.credit')}</Badge>
    ) : (
      <Badge variant="danger">{t('bank.dir.debit')}</Badge>
    );

  const baseColumns = [
    { key: 'valueDate', label: t('bank.col.date'), render: (r) => formatDate(r.valueDate) },
    { key: 'direction', label: t('bank.col.direction'), render: directionBadge },
    { key: 'amount', label: t('bank.col.amount'), align: 'right', render: (r) => formatCurrency(r.amount) },
    { key: 'reference', label: t('bank.col.reference'), render: (r) => r.reference || '-' },
    { key: 'counterparty', label: t('bank.col.counterparty'), render: (r) => r.counterparty || '-' },
  ];

  const unmatchedColumns = [
    ...baseColumns,
    {
      key: 'suggestion',
      label: t('bank.col.suggestion'),
      render: (r) =>
        r.suggestedInvoiceNumber ? (
          <Badge variant="info">{r.suggestedInvoiceNumber}</Badge>
        ) : (
          <span className="muted">-</span>
        ),
    },
    {
      key: 'actions',
      label: '',
      render: (r) => (
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button size="sm" variant="primary" onClick={() => setMatchTx(r)}>{t('bank.action.match')}</Button>
          <Button size="sm" variant="ghost" onClick={() => setIgnoreTx(r)}>{t('bank.action.ignore')}</Button>
        </div>
      ),
    },
  ];

  const columns = tab === 'unmatched' ? unmatchedColumns : baseColumns;
  const emptyMessage = t(`bank.empty.${tab}`);

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('bank.title')}</h1>
          <p className="muted">{t('bank.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setImportOpen(true)}>{t('bank.action.import')}</Button>
        </div>
      </header>

      {stats.count > 0 && (
        <InsightBar
          title={t('insight.byDirection')}
          labels={stats.directions.map((d) => (d === 'CREDIT' ? t('bank.dir.credit') : t('bank.dir.debit')))}
          values={stats.amountByDirection}
          colors={stats.directions.map((d) => (d === 'CREDIT' ? palette?.series?.[1] : palette?.expense))}
          formatValue={(n) => formatCurrency(n)}
          metrics={[
            { label: t('insight.count'), value: stats.count },
            { label: t('bank.insight.inflow'), value: formatCurrency(stats.inflow) },
            { label: t('bank.insight.outflow'), value: formatCurrency(stats.outflow) },
            { label: t('bank.insight.balance'), value: formatCurrency(stats.inflow - stats.outflow) },
          ]}
        />
      )}

      <div className="tab-bar">
        {Object.keys(TAB_STATUS).map((key) => (
          <button
            key={key}
            type="button"
            className={`tab${tab === key ? ' tab-active' : ''}`}
            onClick={() => setTab(key)}
          >
            {t(`bank.tab.${key}`)}
          </button>
        ))}
      </div>

      <Table columns={columns} rows={rows} isLoading={transactions.isLoading} emptyMessage={emptyMessage} />

      <BankImportModal open={importOpen} onClose={() => setImportOpen(false)} />
      <BankMatchModal open={Boolean(matchTx)} transaction={matchTx} onClose={() => setMatchTx(null)} />
      <ConfirmDialog
        open={Boolean(ignoreTx)}
        title={t('bank.ignore.title')}
        message={t('bank.ignore.message')}
        confirmLabel={t('bank.action.ignore')}
        variant="danger"
        isPending={ignore.isPending}
        onConfirm={() => ignoreTx && ignore.mutate(ignoreTx.id)}
        onClose={() => setIgnoreTx(null)}
      />
    </section>
  );
}
